/**
 * The Mage (PRD #800): the class that proves casting.
 *
 * Two things make it different from the other two declarations.
 *
 * **Its implement is its hands.** UO's casting animations are bare-handed, the
 * bake carries no staff or wand, and PRD #800's own rule is that a kit shrinks
 * rather than grows an art budget. So the `channeling` family declares
 * `worn: false`: it contributes a damage band to the derivation fold and puts
 * no item on the weapon layer. That is a change to the class declaration format
 * and it is recorded as a seam finding rather than smuggled in.
 *
 * **Its damage is typed.** Magic Arrow is fire, Lightning is energy, and Nova is
 * cold, so three of the wheel's four elements finally have a consumer and the
 * per-element resists the fold derives are read by something. Poison stays
 * unauthored: the Archer is physical, nothing here is poison, and the class
 * that owns rot is the Necromancer (PRD #804). The four elemental statuses those
 * types eventually feed are out of scope; the Mage deals typed damage and
 * applies none of them.
 *
 * Every number is first-pass and none of it is defended until the tuning PRD
 * (#799).
 */

import type { ArpgClassDef } from './types';

/** The mana cadence the dungeon runs: one point every five simulation ticks. */
const SHIPPED_MANA_REGEN_PER_TICK = 1 / 5;

export const MAGE_CLASS: ArpgClassDef = {
  id: 'mage',
  name: 'Mage',
  blurb: 'A robed Hero who trades commitment for elemental power.',
  playable: true,
  weapons: {
    // Not an item. A damage band for the fold and nothing on the weapon layer,
    // which is what `worn: false` means.
    channeling: { damage: 10, grip: 'one-handed', worn: false },
  },
  allowedEquipmentIds: new Set([
    'robe.plain',
    'hat.wizard',
    'pants.long',
    'sandals.plain',
    'shoes.plain',
    'channeling',
  ]),
  curves: {
    // The frailest of the three, with by far the deepest pool: 80 health to the
    // Warrior's 120, and 90 mana to its 40.
    maxHealth: { base: 80, perLevel: 4 },
    maxMana: { base: 90, perLevel: 6 },
    healthRegenPerTick: 0,
    // Casting is the class's whole economy, so the baseline cadence is half
    // again the Warrior's before a point of Wisdom is spent.
    manaRegenPerTick: SHIPPED_MANA_REGEN_PER_TICK * 1.5,
    critChance: 0,
    critMultiplier: 1.5,
  },
  abilities: [
    { abilityId: 'mage-magic-arrow', unlockLevel: 1, slot: 'right-mouse' },
    { abilityId: 'mage-lightning', unlockLevel: 3, slot: 'left-mouse' },
    { abilityId: 'mage-nova', unlockLevel: 5, slot: 'key-3' },
    { abilityId: 'mage-blink', unlockLevel: 7, slot: 'key-4' },
    { abilityId: 'mage-ward', unlockLevel: 9, slot: 'key-5' },
  ],
  startingEquipment: {
    mainHandId: 'channeling',
    equipped: [
      { id: 'robe.plain', layer: 'robe', hue: 1266 },
      { id: 'hat.wizard', layer: 'headgear', hue: 1266 },
      { id: 'sandals.plain', layer: 'feet' },
    ],
  },
  balanceFloor: {
    // Magic Arrow deals 17 and recovers in ten ticks, so the 70-health
    // placeholder dies on the fifth bolt, forty ticks of cooldown plus the
    // six-tick wind-up each one commits to and the flight across the gap.
    maxTicksToKillPlaceholder: 80,
    // 80 health against a twelve-damage swing is seven hits. A Mage in melee is
    // already losing; the floor only says it is not one-shot.
    minPlaceholderSwingsSurvived: 6,
    // Nova is the costliest thing the kit casts, at eighteen of a ninety pool.
    minCostliestCastsFromFullMana: 5,
  },
};
